import { useState } from "react";
import { approveInbound, rejectInbound } from "./api";

/**
 * Granskningsvyn för skolans utskick (AA-5d).
 *
 * AA-5d är etappens ryggrad: **ingenting når kunskapsbasen eller appen utan att
 * en människa klickat.** Den här vyn är det klicket, och därför är den skriven
 * för att gå långsamt att använda — inget "godkänn alla", ingen förkryssad
 * kryssruta när något ser ut att bära elevuppgifter, ingen knapp som gör två
 * saker.
 *
 * ## Formen är uppladdningsformulärets, med flit
 *
 * AA-5.4: *"allt förifyllt och redigerbart i granskningsvyn, som ÄR
 * uppladdningsformuläret: en rad per dokument, egen titel, eget valid_until, en
 * Ta bort-knapp. Formen finns sedan 2026-08-29 och ska återanvändas, inte ritas
 * om."*
 *
 * ## Tre fält som ser ut som pynt och inte är det
 *
 * - **`d=` och `s=`** är avsändaridentiteten sådan kontrollen fastställde den,
 *   inte sådan meddelandet påstod den. Den som undrar varför ett utskick kom
 *   fram ska kunna se det utan att fråga oss.
 * - **Sidfotsavdraget** skriver ut hur många tecken som klipptes. Det är enda
 *   stället ett för aggressivt avdrag går att upptäcka innan utvärderingssetet
 *   körs — se `mail_ingest.strip_footer`.
 * - **Bilagorna** listas med orsak när formatet inte stöds. AA-5.4 kräver
 *   läsbar orsak i granskningsvyn, aldrig tystnad.
 */

/** AA-5.3:s förhandsvarningar, översatta. Nycklarna kommer från backenden. */
const FLAGGTEXT = {
  personnummer: "Texten innehåller något som ser ut som ett personnummer.",
  klassbeteckning:
    "Texten nämner en klassbeteckning. NA21B är en kvasiidentifierare i en " +
    "skola med två hundra elever per årskurs.",
  elevhalsa: "Texten nämner elevhälsa eller stöd.",
};

function Flaggor({ flags }) {
  if (!flags?.length) return null;
  return (
    <ul className="warn-list">
      {flags.map((flag) => (
        <li key={flag}>{FLAGGTEXT[flag] || flag}</li>
      ))}
    </ul>
  );
}

function Bilagor({ attachments }) {
  if (!attachments?.length) return null;
  return (
    <>
      <h4>Bilagor</h4>
      <ul className="tally">
        {attachments.map((bilaga, index) => (
          <li key={index}>
            {bilaga.filename}
            {bilaga.supported ? (
              // Luckan skrivs ut i stället för att tigas ihjäl. Se
              // inbound_review-modulens docstring.
              <span className="hint">
                {" "}— {Math.round((bilaga.bytes || 0) / 1024)} kB. Bilagan
                indexeras <strong>inte</strong> ännu; bara meddelandets text
                blir ett dokument.
              </span>
            ) : (
              <span className="hint"> — {bilaga.reason}</span>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function Meddelande({ user, message, onChange }) {
  const [title, setTitle] = useState(message.subject || "");
  // H7: förvalet är modellens förslag inom taket, annars taket. Det räknas i
  // backenden — en regel som bara finns i ett formulär är ingen regel — och
  // fältet är förifyllt men aldrig tomt.
  const [validUntil, setValidUntil] = useState(message.suggested_valid_until);
  // AA-5.3: markeringen väljer inte bort meddelandet, den tar bort
  // förkryssningen. Är något flaggat måste granskaren kryssa själv.
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const flaggat = message.flags?.length > 0;

  async function decide(action) {
    setBusy(action);
    setError(null);
    try {
      if (action === "approve") {
        await approveInbound(user, message.id, {
          title,
          valid_until: validUntil,
          no_personal_data: confirmed,
        });
      } else {
        await rejectInbound(user, message.id);
      }
      await onChange();
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  }

  return (
    <article className="inbound">
      <h3>{message.subject || <span className="hint">(utan ämnesrad)</span>}</h3>
      <p className="hint">
        Mottaget {message.received_at} · {message.dkim}
      </p>

      {flaggat && (
        <div className="warn">
          <strong>Läs extra noga.</strong> Det här väljer inte bort utskicket —
          det betyder att kryssrutan nedan inte är förkryssad och att du måste
          ta ställning själv.
          <Flaggor flags={message.flags} />
        </div>
      )}

      {/* Texten som blir dokumentet. Inte en förhandsvisning av mejlet — det
          här ÄR vad eleverna kommer att kunna få som svar. */}
      <pre className="body">{message.body_text}</pre>

      <Bilagor attachments={message.attachments} />

      <label>
        Titel
        <input value={title} onChange={(e) => setTitle(e.target.value)}
               placeholder={message.subject} />
      </label>
      <p className="hint">
        Ämnesraden är förvald. Den är skriven av en människa för andra
        människor, och är ett bättre sökunderlag än ett filnamn.
      </p>

      <label>
        Sista giltighetsdag
        <input type="date" value={validUntil} max={message.max_valid_until}
               onChange={(e) => setValidUntil(e.target.value)} required />
      </label>
      <p className="hint">
        Obligatorisk för utskick, till skillnad från uppladdningar, och kan inte
        sättas senare än {message.max_valid_until} — 120 dagar efter mottagandet.
        Utskick löper alltid ut; varaktiga rutiner hör hemma i ett uppladdat
        dokument.
      </p>

      {/* AA-6.3:s kryssruta, flyttad. Ett mejl har ingen att ärva — den som
          skrev utskicket skrev till sin lista och visste inte att Aerro låg i
          den. Ansvaret ligger därför på den som LÄSER. */}
      <label className="confirm">
        <input type="checkbox" checked={confirmed}
               onChange={(e) => setConfirmed(e.target.checked)} />
        Jag har läst utskicket och det innehåller inga personuppgifter om
        enskilda elever.
      </label>

      {error && <p className="error">{error}</p>}

      <div className="actions">
        <button disabled={!confirmed || !validUntil || busy}
                onClick={() => decide("approve")}>
          {busy === "approve" ? "Godkänner…" : "Godkänn och indexera"}
        </button>
        <button className="danger" disabled={busy}
                onClick={() => decide("reject")}>
          {busy === "reject" ? "Avvisar…" : "Avvisa"}
        </button>
      </div>
      <p className="hint">
        Ett avvisat utskick skrivs inte någonstans och kan inte tas tillbaka.
        Avsändaren får ingen återkoppling — adressen tar emot och svarar aldrig.
      </p>
    </article>
  );
}

export default function Inbound({ user, data, onChange }) {
  if (!data) return null;
  const messages = data.messages || [];

  return (
    <section className="card">
      <h2>Utskick att granska</h2>
      <p className="hint">
        Skolans utskick från Meitner. Ingenting här når eleverna förrän någon
        godkänt det, och varje utskick som godkänns får ett sista datum.
      </p>

      {messages.length === 0 ? (
        <p className="empty">Inga utskick väntar på granskning.</p>
      ) : (
        messages.map((message) => (
          <Meddelande key={message.id} user={user} message={message}
                      onChange={onChange} />
        ))
      )}
    </section>
  );
}
